/**
 * CandidatesSidebar - Left sidebar displaying candidate list
 *
 * Shows candidates from the selected configuration.
 * Each candidate displays principal and efficiency metrics.
 * Supports starring favorites and viewing favorites grouped by configuration.
 * Super admins can toggle viewing failed (non-viable) candidates.
 *
 * Sorting:
 * - Revenue: sorts all candidates together (viable + non-viable mixed)
 * - Other options: viable candidates first, then non-viable at end
 */

import { Box, HStack, IconButton, NativeSelect, Text, VStack } from '@chakra-ui/react';
import type { FavoriteCandidateInfo } from '@shared/models';
import { AlertTriangle, Check, Star } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import type { Candidate } from '~/types/candidate';
import type { Configuration } from '~/types/project';

type SortOption = 'revenue' | 'principal' | 'utilization' | 'efficiency';

export interface CandidatesSidebarProps {
  /** List of candidates to display */
  candidates: Candidate[] | null;

  /** Currently selected candidate ID */
  selectedCandidateId: string | null;

  /** Handler called when a candidate is selected */
  onCandidateSelect: (id: string) => void;

  /** Current configuration ID (for favorites) */
  configurationId: string | null;

  /** All favorites for the project */
  favorites: FavoriteCandidateInfo[];

  /** Handler to toggle favorite status */
  onToggleFavorite: (shapeId: string) => void;

  /** Whether showing favorites view */
  showFavoritesView: boolean;

  /** Handler to toggle favorites view */
  onToggleFavoritesView: () => void;

  /** All configurations (for favorites view grouping) */
  allConfigurations?: Configuration[] | null;

  /** Handler when a favorite is selected (navigates to config + candidate) */
  onFavoriteSelect?: (configId: string, shapeId: string) => void;

  /** Whether user is a super admin (enables failed candidates view) */
  isSuperAdmin?: boolean;

  /** Failed candidates for super admin debugging */
  failedCandidates?: Candidate[] | null;
}

/**
 * Get background color for candidate based on utilization and viability
 */
function getCandidateBackgroundColor(candidate: Candidate): string {
  const { results } = candidate;

  // Non-viable candidates get red background
  if (!results || !results.isViable) {
    return 'red.50';
  }

  // Utilization is a ratio 0-1+, where 0.98 = 98% utilization, can exceed 1.0 (100%+)
  const utilization = results.revenueUtilization;

  // Handle undefined/null utilization
  if (utilization === undefined || utilization === null || Number.isNaN(utilization)) {
    return 'white'; // Default to white
  }

  if (utilization >= 0.98) {
    return 'green.50'; // Light green for exceptional (≥98%)
  } else {
    return 'white'; // White for everything else
  }
}

/**
 * Get shapeId from candidate
 */
function getShapeId(candidate: Candidate): string {
  return candidate.millageInformation?.millageShape?.shapeId || '';
}

export const CandidatesSidebar: React.FC<CandidatesSidebarProps> = ({
  candidates,
  selectedCandidateId,
  onCandidateSelect,
  configurationId,
  favorites,
  onToggleFavorite,
  showFavoritesView,
  onToggleFavoritesView,
  allConfigurations,
  onFavoriteSelect,
  isSuperAdmin = false,
  failedCandidates,
}) => {
  const [sortBy, setSortBy] = useState<SortOption>('principal');
  // Default to not showing failed candidates (super admins can toggle to show them)
  const [showFailed, setShowFailed] = useState(false);

  // Format currency for display
  const formatCurrency = useCallback((amount: number): string => {
    if (amount >= 1_000_000) {
      return `$${(amount / 1_000_000).toFixed(1)}M`;
    }
    return `$${(amount / 1_000).toFixed(0)}K`;
  }, []);

  // Combine and sort candidates based on sort option
  // Revenue: sort all together (viable + non-viable mixed by revenue)
  // Other options: viable first, then non-viable at end
  const sortedCandidates = useMemo(() => {
    if (!candidates) return [];

    // Build the list of candidates to display
    const allCandidates = [...candidates];

    // Add failed candidates if showing them
    if (showFailed && failedCandidates) {
      allCandidates.push(...failedCandidates);
    }

    // Define primary sort function
    const getPrimaryValue = (c: Candidate): number => {
      switch (sortBy) {
        case 'revenue':
          return c.results.totalRevenue;
        case 'principal':
          return c.results.totalPrincipal;
        case 'utilization':
          return c.results.revenueUtilization;
        case 'efficiency':
          return c.results.efficiency;
      }
    };

    // Define secondary sort function
    const getSecondaryValue = (c: Candidate): number => {
      // Revenue and Principal use utilization as secondary
      // Utilization and Efficiency use principal as secondary
      if (sortBy === 'revenue' || sortBy === 'principal') {
        return c.results.revenueUtilization;
      }
      return c.results.totalPrincipal;
    };

    return allCandidates.sort((a, b) => {
      // For revenue sort: mix viable and non-viable together
      // For other sorts: viable first, then non-viable
      if (sortBy !== 'revenue') {
        if (a.results.isViable && !b.results.isViable) return -1;
        if (!a.results.isViable && b.results.isViable) return 1;
      }

      // Primary sort (descending)
      const primaryDiff = getPrimaryValue(b) - getPrimaryValue(a);
      if (primaryDiff !== 0) return primaryDiff;

      // Secondary sort (descending)
      return getSecondaryValue(b) - getSecondaryValue(a);
    });
  }, [candidates, failedCandidates, showFailed, sortBy]);

  // Check if a candidate is favorited
  const isFavorited = useCallback(
    (shapeId: string): boolean => {
      if (!configurationId) return false;
      return favorites.some((f) => f.configurationId === configurationId && f.shapeId === shapeId);
    },
    [configurationId, favorites]
  );

  // Group favorites by configuration for favorites view
  const groupedFavorites = useMemo(() => {
    if (!showFavoritesView || !allConfigurations) return null;

    const groups = new Map<string, { config: Configuration; favorites: FavoriteCandidateInfo[] }>();

    for (const fav of favorites) {
      const config = allConfigurations.find((c) => c.id === fav.configurationId);
      if (!config) continue;

      if (!groups.has(fav.configurationId)) {
        groups.set(fav.configurationId, { config, favorites: [] });
      }
      groups.get(fav.configurationId)!.favorites.push(fav);
    }

    return Array.from(groups.values()).sort((a, b) => {
      const dateA = new Date(a.config.createdAt).getTime();
      const dateB = new Date(b.config.createdAt).getTime();
      return dateA - dateB; // Sort by createdAt (oldest first)
    });
  }, [showFavoritesView, allConfigurations, favorites]);

  // Render star icon for a candidate
  const renderStar = (shapeId: string, isStarred: boolean) => (
    <IconButton
      size="xs"
      variant="ghost"
      aria-label={isStarred ? 'Remove from favorites' : 'Add to favorites'}
      onClick={(e) => {
        e.stopPropagation();
        onToggleFavorite(shapeId);
      }}
      minW="24px"
      h="24px"
      p={0}
    >
      <Star
        size={14}
        fill={isStarred ? '#F6E05E' : 'none'}
        color={isStarred ? '#D69E2E' : '#A0AEC0'}
        style={{ transition: 'fill 0.15s ease-in-out, color 0.15s ease-in-out' }}
      />
    </IconButton>
  );

  // Render a candidate row
  const renderCandidateRow = (
    candidate: Candidate,
    isSelected: boolean,
    showStar: boolean = true,
    key?: string
  ) => {
    const principal = formatCurrency(candidate.results.totalPrincipal);
    const utilization = (candidate.results.revenueUtilization * 100).toFixed(1);
    const efficiency = (candidate.results.efficiency * 100).toFixed(1);
    const bgColor = getCandidateBackgroundColor(candidate);
    const isNonViable = !candidate.results?.isViable;
    const shapeId = getShapeId(candidate);
    const isStarred = isFavorited(shapeId);

    // Get border styling based on selection and viability
    const getBorderColor = () => {
      if (isSelected) return 'gray.800'; // Black border for selected
      if (isNonViable) return 'red.200';
      return 'gray.200';
    };

    const getBorderWidth = () => {
      if (isSelected) return '2px';
      return '1px';
    };

    return (
      <Box
        key={key}
        p={isSelected ? 2.5 : 2} // Slightly larger padding when selected
        borderRadius="md"
        bg={bgColor} // Keep original background, don't change for selection
        borderWidth={getBorderWidth()}
        borderColor={getBorderColor()}
        boxShadow={isSelected ? 'md' : 'none'} // Shadow for selected
        cursor="pointer"
        _hover={{
          bg: bgColor === 'transparent' ? 'gray.50' : bgColor,
          borderColor: isSelected ? 'gray.900' : isNonViable ? 'red.300' : 'gray.300',
        }}
        onClick={() => onCandidateSelect(candidate.id)}
        transition="all 0.2s"
      >
        <HStack gap={2} align="start">
          {/* Star icon */}
          {showStar && configurationId && renderStar(shapeId, isStarred)}

          {/* Candidate data */}
          <VStack align="stretch" gap={0.5} flex={1}>
            {/* Non-viable indicator */}
            {isNonViable && (
              <Box
                bg="red.100"
                color="red.700"
                fontSize="xs"
                fontWeight="semibold"
                px={2}
                py={0.5}
                borderRadius="sm"
                textAlign="center"
                mb={1}
              >
                Non-Viable
              </Box>
            )}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Text fontSize="xs" color="gray.500">
                Principal
              </Text>
              <Text fontSize="sm" fontWeight={isSelected ? 'semibold' : 'normal'} color="gray.700">
                {principal}
              </Text>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Text fontSize="xs" color="gray.500">
                Utilization
              </Text>
              <Text fontSize="sm" fontWeight={isSelected ? 'semibold' : 'normal'} color="gray.700">
                {utilization}%
              </Text>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Text fontSize="xs" color="gray.500">
                Efficiency
              </Text>
              <Text fontSize="sm" fontWeight={isSelected ? 'semibold' : 'normal'} color="gray.700">
                {efficiency}%
              </Text>
            </Box>
          </VStack>
        </HStack>
      </Box>
    );
  };

  // Render favorites view (grouped by configuration)
  const renderFavoritesView = () => {
    if (!groupedFavorites || groupedFavorites.length === 0) {
      return (
        <Box p={4} textAlign="center">
          <Star size={24} color="#D69E2E" style={{ margin: '0 auto 8px' }} />
          <Text fontSize="sm" color="gray.600" mb={1}>
            No favorites yet
          </Text>
          <Text fontSize="xs" color="gray.400">
            Click the star icon on any candidate to save it for later
          </Text>
        </Box>
      );
    }

    return (
      <VStack align="stretch" gap={3}>
        {groupedFavorites.map(({ config, favorites: configFavs }) => (
          <Box key={config.id}>
            {/* Configuration header */}
            <Box
              py={2}
              px={2}
              bg="gray.50"
              borderRadius="md"
              mb={1}
              cursor="pointer"
              _hover={{ bg: 'gray.100' }}
              onClick={() => {
                if (onFavoriteSelect && configFavs.length > 0) {
                  onFavoriteSelect(config.id, configFavs[0].shapeId);
                }
              }}
            >
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                {config.name || `Config ${config.orderId || 1}`}
              </Text>
            </Box>

            {/* Favorited candidates in this config */}
            <VStack align="stretch" gap={1} pl={1}>
              {configFavs.map((fav) => {
                // Find the candidate with this shapeId
                const candidate = config.candidates?.find((c) => getShapeId(c) === fav.shapeId);

                if (!candidate) {
                  // Shape no longer exists in this config
                  return (
                    <Box
                      key={fav.shapeId}
                      py={1}
                      px={2}
                      opacity={0.5}
                      display="flex"
                      alignItems="center"
                      gap={2}
                    >
                      <Star size={12} fill="#F6E05E" color="#D69E2E" />
                      <Text fontSize="xs" color="gray.500" fontStyle="italic">
                        Shape no longer available
                      </Text>
                    </Box>
                  );
                }

                const principal = formatCurrency(candidate.results.totalPrincipal);
                const utilization = (candidate.results.revenueUtilization * 100).toFixed(1);

                return (
                  <Box
                    key={fav.shapeId}
                    py={1}
                    px={2}
                    borderRadius="sm"
                    cursor="pointer"
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => {
                      if (onFavoriteSelect) {
                        onFavoriteSelect(config.id, fav.shapeId);
                      }
                    }}
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    <Star size={12} fill="#F6E05E" color="#D69E2E" />
                    <Text fontSize="xs" color="gray.700">
                      {principal}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      |
                    </Text>
                    <Text fontSize="xs" color="gray.700">
                      {utilization}%
                    </Text>
                  </Box>
                );
              })}
            </VStack>
          </Box>
        ))}
      </VStack>
    );
  };

  // Count of failed candidates
  const failedCount = failedCandidates?.length || 0;

  return (
    <Box
      width={LAYOUT_SPACING.SIDEBAR_EXPANDED_SM}
      minWidth={LAYOUT_SPACING.SIDEBAR_EXPANDED_SM}
      height="100%"
      bg="white"
      borderRight="1px solid"
      borderColor="gray.200"
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {/* Row 1: Header */}
      <Box px={3} pt={3} pb={2} display="flex" width="100%" justifyContent="center">
        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
          Candidates
        </Text>
      </Box>

      {/* Row 2: Filter toggles */}
      <Box px={3} pb={2} borderBottom="1px solid" borderColor="gray.200" width="100%">
        <HStack gap={3} justify="space-around">
          {/* Favorites toggle */}
          <HStack
            gap={1.5}
            cursor="pointer"
            onClick={onToggleFavoritesView}
            opacity={showFavoritesView ? 1 : 0.6}
            _hover={{ opacity: 1 }}
          >
            <Star
              size={14}
              fill={showFavoritesView ? '#F6E05E' : 'none'}
              color={showFavoritesView ? '#D69E2E' : '#A0AEC0'}
            />
            <Text fontSize="xs" color={showFavoritesView ? 'yellow.700' : 'gray.600'}>
              Favorites
            </Text>
          </HStack>

          {/* Show Failed toggle - super admin only */}
          {isSuperAdmin && failedCount > 0 && (
            <HStack
              gap={1.5}
              cursor="pointer"
              onClick={() => setShowFailed(!showFailed)}
              opacity={showFailed ? 1 : 0.6}
              _hover={{ opacity: 1 }}
            >
              <Box
                w="14px"
                h="14px"
                borderRadius="sm"
                borderWidth="1px"
                borderColor={showFailed ? 'red.500' : 'gray.300'}
                bg={showFailed ? 'red.500' : 'transparent'}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {showFailed && <Check size={10} color="white" />}
              </Box>
              <AlertTriangle size={12} color="#C53030" />
              <Text fontSize="xs" color="red.600">
                Failed ({failedCount})
              </Text>
            </HStack>
          )}
        </HStack>
      </Box>

      {/* Row 3: Sort dropdown */}
      {!showFavoritesView && candidates && candidates.length > 0 && (
        <Box px={3} py={2} borderBottom="1px solid" borderColor="gray.100" bg="gray.50">
          <HStack gap={2}>
            <Text fontSize="xs" color="gray.500" flexShrink={0}>
              Sort:
            </Text>
            <NativeSelect.Root size="xs" flex={1}>
              <NativeSelect.Field
                value={sortBy}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setSortBy(e.target.value as SortOption)
                }
                bg="white"
                fontSize="xs"
              >
                <option value="revenue">Revenue (Shape)</option>
                <option value="principal">Principal</option>
                <option value="utilization">Utilization</option>
                <option value="efficiency">Efficiency</option>
              </NativeSelect.Field>
            </NativeSelect.Root>
          </HStack>
        </Box>
      )}

      {/* Candidate/Favorites list */}
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        p={3}
        css={{
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#CBD5E0',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#A0AEC0',
          },
        }}
      >
        {showFavoritesView ? (
          renderFavoritesView()
        ) : !candidates || candidates.length === 0 ? (
          // Empty state
          <Box p={4} textAlign="center">
            <Text fontSize="sm" color="gray.500" mb={1}>
              No candidates yet
            </Text>
            <Text fontSize="xs" color="gray.400">
              Process a configuration to see candidates
            </Text>
          </Box>
        ) : (
          // Candidate list (already sorted with viable/non-viable handling)
          <VStack align="stretch" gap={2}>
            {sortedCandidates.map((candidate) =>
              renderCandidateRow(
                candidate,
                selectedCandidateId === candidate.id,
                true,
                candidate.id
              )
            )}
            <Box height="50px" />
          </VStack>
        )}
      </Box>
    </Box>
  );
};
